/* eslint-disable import/no-extraneous-dependencies */
import AppModal from '@/components/AppModal/AppModal';
import { config } from '@/config';
import { DEFAULT_ORDER_COLUMN } from '@/constants';
import { useGetGeneralSettingQuery, useUpdateGeneralSettingMutation } from '@/redux/query';
import { Box, ChoiceList, Icon, InlineStack } from '@shopify/polaris';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import BoldText from '@/components/BoldText';
import RegularText from '@/components/RegularText';
import { DragHandleIcon } from '@shopify/polaris-icons';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';

interface Props {
  open: boolean;
  onSubmit: () => void;
  onCancel: () => void;
  showColumns: string[];
  orderColumns: {
    key: string;
    label: string;
    visible: boolean;
  }[];
}

export const EditColumnModal = ({ open, onSubmit, onCancel, showColumns, orderColumns }: Props) => {
  const DEFAULT_SORT_ORDER_COLUMN = useMemo(
    () => ['order', 'createdAt', 'item', 'total', 'payment', 'dueDate', 'method', 'location', 'shipTo', 'status', 'customer'],
    [],
  );

  const { t } = useTranslation('orderReport');
  const [selected, setSelected] = useState<string[]>(showColumns);
  const [orderSelected, setOrderSelected] = useState<string[]>(DEFAULT_SORT_ORDER_COLUMN);
  const { refetch: refetchGeneralSetting } = useGetGeneralSettingQuery({ shop: config.shop });
  const [updateGeneralSetting, { isLoading: isLoadingGeneralSetting }] = useUpdateGeneralSettingMutation();

  const handleChange = useCallback(
    (value: string[]) => {
      if (selected.length === 1 && value.length === 0) {
        return;
      }
      setSelected(value);
    },
    [selected],
  );

  const choices = useMemo(
    () => [
      { label: t('reportTable.column.order'), value: 'order', disabled: selected.length === 1 && selected.includes('order') },
      {
        label: t('reportTable.column.created'),
        value: 'createdAt',
        disabled: selected.length === 1 && selected.includes('createdAt'),
      },
      { label: t('reportTable.column.item'), value: 'item', disabled: selected.length === 1 && selected.includes('item') },
      { label: t('reportTable.column.total'), value: 'total', disabled: selected.length === 1 && selected.includes('total') },
      { label: t('reportTable.column.pay'), value: 'payment', disabled: selected.length === 1 && selected.includes('payment') },
      { label: t('reportTable.column.date'), value: 'dueDate', disabled: selected.length === 1 && selected.includes('dueDate') },
      { label: t('reportTable.column.method'), value: 'method', disabled: selected.length === 1 && selected.includes('method') },
      {
        label: t('reportTable.column.location'),
        value: 'location',
        disabled: selected.length === 1 && selected.includes('location'),
      },
      { label: t('reportTable.column.ship'), value: 'shipTo', disabled: selected.length === 1 && selected.includes('shipTo') },
      { label: t('reportTable.column.status'), value: 'status', disabled: selected.length === 1 && selected.includes('status') },
      {
        label: t('reportTable.column.customer'),
        value: 'customer',
        disabled: selected.length === 1 && selected.includes('customer'),
      },
    ],
    [selected, t],
  );

  const deepCopiedChoices = useMemo(() => choices.map((choice) => ({ ...choice })), [choices]);

  const handleDragEnd = useCallback(
    (result: any) => {
      const { source, destination } = result;

      if (!destination) {
        return;
      }

      const reorderedItems = Array.from(orderSelected);
      const [movedItem] = reorderedItems.splice(source.index, 1);
      reorderedItems.splice(destination.index, 0, movedItem);

      setOrderSelected(reorderedItems);
    },
    [orderSelected],
  );

  const updatedColumns = DEFAULT_ORDER_COLUMN.map((column) => ({
    ...column,
    visible: selected.includes(column.key),
  })).sort((a, b) => {
    const indexA = orderSelected.indexOf(a.key);
    const indexB = orderSelected.indexOf(b.key);

    return indexA - indexB;
  });

  const sortedChoices = deepCopiedChoices.sort(
    (a: { label: string; value: string; disabled: boolean }, b: { label: string; value: string; disabled: boolean }) => {
      const indexA = orderSelected.indexOf(a.value);
      const indexB = orderSelected.indexOf(b.value);
      return indexA - indexB;
    },
  );

  useEffect(() => {
    setOrderSelected(
      DEFAULT_SORT_ORDER_COLUMN.sort((a, b) => {
        const indexA = orderColumns.findIndex((col) => col.key === a);
        const indexB = orderColumns.findIndex((col) => col.key === b);
        return indexA - indexB;
      }),
    );
  }, [DEFAULT_SORT_ORDER_COLUMN, orderColumns]);

  return (
    <AppModal
      open={open}
      onCancel={() => {
        onCancel();
        setSelected(showColumns);
        setOrderSelected(
          DEFAULT_SORT_ORDER_COLUMN.sort((a, b) => {
            const indexA = orderColumns.findIndex((col) => col.key === a);
            const indexB = orderColumns.findIndex((col) => col.key === b);
            return indexA - indexB;
          }),
        );
      }}
      onSubmit={() => {
        updateGeneralSetting({
          shop: config.shop,
          orderColumn: updatedColumns,
        }).then(() => {
          refetchGeneralSetting();
          onCancel();
        });
        onSubmit();
      }}
      title={'Customize Columns'}
      submitText={'Submit'}
      cancelText={'Cancel'}
      loading={isLoadingGeneralSetting}
    >
      <InlineStack gap={'400'}>
        <BoldText>Select Columns</BoldText>
        <BoldText>Order Columns</BoldText>
      </InlineStack>
      <Box paddingBlockStart="400">
        <InlineStack gap={'800'}>
          <div style={{ marginTop: '-8px' }}>
            <ChoiceList
              title="Select Columns"
              titleHidden
              choices={choices}
              selected={selected}
              onChange={handleChange}
              allowMultiple
            />
          </div>
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="columns" direction="vertical">
              {(provided) => (
                <div {...provided.droppableProps} ref={provided.innerRef} style={{ flex: 1 }}>
                  {sortedChoices.map((choice, index) => (
                    <Draggable key={choice.value} draggableId={choice.value} index={index}>
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          style={{
                            ...provided.draggableProps.style,
                            marginBottom: '8px',
                            display: selected.includes(choice.value) ? 'flex' : 'none',
                            gap: '8px',
                            alignItems: 'center',
                            padding: '8px',
                            background: snapshot.isDragging ? '#f0f0f0' : 'white',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                          }}
                        >
                          <Box>
                            <Icon source={DragHandleIcon} />
                          </Box>
                          <RegularText>{choice.label}</RegularText>
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </InlineStack>
      </Box>
    </AppModal>
  );
};

export default EditColumnModal;
